import { VStack, HStack, Heading, Box, Stack, Center } from "@chakra-ui/react";
import CustomTabList from "../../../CustomUi/CustomTabList";
import { BiPlus } from "react-icons/bi";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import { Level, Subject, BehavioralSubject } from "../../../../services/Dtos";
import { useState } from "react";
import Loader from "../../../CustomUi/Loader";
import CustomAlert from "../../../CustomUi/CustomAlert";
import SubjectModal from "./SubjectModal";
import SubjectTable from "./SubjectTable";
import Empty from "../../../CustomUi/Empty";
import { useNavigate } from "react-router-dom";
import BehavioralSubjectTable from "../BehavioralSubjectSettings/BehavioralSubjectTable";
import BehavioralSubjectModal from "../BehavioralSubjectSettings/BehavioralSubjectModal";
import useThemeColors from "../../../../hooks/useThemeColors";
import SectionHeader from "../../../CustomUi/SectionHeader";
import useIcons from "../../../../hooks/useIcons";
import Cad from "../../../CustomUi/Cad";
import CustomButton from "../../../CustomUi/CustomButton";
import { useDisclosure } from "@chakra-ui/react";

function SubjectSettings() {
  const { levels, isLoading, error, accentColor } = useSchoolsContext();

  const colors = useThemeColors();
  const icons = useIcons();
  const navigate = useNavigate();

  // Subject state
  const [subjectToEdit, setSubjectToEdit] = useState<Subject | null>(null);
  const [subjectLevel, setSubjectLevel] = useState<Level | null>(null);
  const {
    open: openSubjectModal,
    onOpen: onOpenSubjectModal,
    onClose: onCloseSubjectModal,
  } = useDisclosure();

  // Behavioral Subject state
  const [behavioralSubjectToEdit, setBehavioralSubjectToEdit] =
    useState<BehavioralSubject | null>(null);
  const [behavioralSubjectLevel, setBehavioralSubjectLevel] =
    useState<Level | null>(null);
  const {
    open: openBehavioralSubjectModal,
    onOpen: onOpenBehavioralSubjectModal,
    onClose: onCloseBehavioralSubjectModal,
  } = useDisclosure();

  const handleRefresh = () => {
    // Refresh functionality can be implemented here
  };

  const handleAddSubject = (level: Level) => {
    setSubjectToEdit(null);
    setSubjectLevel(level);
    onOpenSubjectModal();
  };

  const handleAddBehavioralSubject = (level: Level) => {
    setBehavioralSubjectToEdit(null);
    setBehavioralSubjectLevel(level);
    onOpenBehavioralSubjectModal();
  };

  const handleEditSubject = (subject: Subject) => {
    setSubjectToEdit(subject);
    setSubjectLevel(levels.find((l) => l.id === subject.levelId) || null);
    onOpenSubjectModal();
  };

  const handleEditBehavioralSubject = (
    behavioralSubject: BehavioralSubject
  ) => {
    setBehavioralSubjectToEdit(behavioralSubject);
    setBehavioralSubjectLevel(
      levels.find((l) => l.id === behavioralSubject.levelId) || null
    );
    onOpenBehavioralSubjectModal();
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <CustomAlert message={error} handleClick={handleRefresh} />;
  }

  return (
    <VStack gap={8} h="100%">
      <SubjectModal
        isOpen={openSubjectModal}
        onClose={onCloseSubjectModal}
        subjectToEdit={subjectToEdit}
        subjectLevel={subjectLevel}
      />
      <BehavioralSubjectModal
        isOpen={openBehavioralSubjectModal}
        onClose={onCloseBehavioralSubjectModal}
        behavioralSubjectToEdit={behavioralSubjectToEdit}
        behavioralSubjectLevel={behavioralSubjectLevel}
      />
      {levels.length > 0 ? (
        <Cad minimal>
          <VStack gap={6} align="stretch">
            <SectionHeader
              title="School Subjects"
              subtitle="Manage school subjects for all school levels"
              icon={icons.main.subject}
            />

            <CustomTabList
              items={levels.map((level) => ({
                id: level.id,
                label: level.levelName,
              }))}
              colorPalette={colors.schoolAccent.primary}
            >
              {(item) => {
                const level = levels.find((l) => l.id === item.id);
                if (!level) return null;

                return (
                  <Stack
                    direction={{ base: "column", lg: "row" }}
                    gap={8}
                    align="stretch"
                    w="full"
                  >
                    {/* Cognitive Subjects Section */}
                    <Box
                      flex={1}
                      bg={colors.background.secondary}
                      p={0}
                      borderRadius="lg"
                    >
                      <HStack
                        justify="space-between"
                        align="center"
                        mb={4}
                        flexDir={["column", "row"]}
                        alignItems={["stretch", "center"]}
                      >
                        <Heading size="md" color={colors.text.primary}>
                          {item.label} Cognitive Subjects
                        </Heading>
                        <CustomButton
                          icon={icons.utility.plus}
                          colorPalette={accentColor}
                          label="Add Subject"
                          onClick={() => handleAddSubject(level)}
                          w={{ base: "100%", md: "auto" }}
                        />
                      </HStack>
                      <SubjectTable
                        handleEditSubject={handleEditSubject}
                        level={level}
                      />
                    </Box>
                    {/* Behavioral Subjects Section */}
                    <Box
                      flex={1}
                      bg={colors.background.secondary}
                      p={0}
                      borderRadius="lg"
                    >
                      <HStack
                        justify="space-between"
                        align="center"
                        mb={4}
                        flexDir={["column", "row"]}
                        alignItems={["stretch", "center"]}
                      >
                        <Heading size="md" color={colors.text.primary}>
                          {level.levelName} Behavioral Subjects
                        </Heading>
                        <CustomButton
                          icon={BiPlus}
                          colorPalette={accentColor}
                          label="Add Subject"
                          onClick={() => handleAddBehavioralSubject(level)}
                          w={{ base: "100%", md: "auto" }}
                        />
                      </HStack>
                      <BehavioralSubjectTable
                        handleEditBehavioralSubject={
                          handleEditBehavioralSubject
                        }
                        level={level}
                      />
                    </Box>
                  </Stack>
                );
              }}
            </CustomTabList>
          </VStack>
        </Cad>
      ) : (
        <Center h="100%">
          <Empty
            hasButton
            message="Something will show up here when you set up School Levels"
            buttonText="Go to Levels Settings"
            handleClick={() => navigate("/settings/level")}
          />
        </Center>
      )}
    </VStack>
  );
}

export default SubjectSettings;
